import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';

import { FormField } from '@/App/tiles/ForwardAccumulator/parts/RightPanel/common/FormField';
import {
  useCheckForwardAccumulatorByTradeCapture,
  useForwardAccumulatorSelector,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput/index.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import {
  selectForwardAccumulatorHedgeData,
  selectForwardAccumulatorProperty,
  selectForwardAccumulatorRfsHedgeAmount,
  selectForwardAccumulatorValidation,
} from '@/store/forwardAccumulator/selectors.ts';
import { useAppSelector } from '@/store/hooks.ts';

export function HedgeAmount() {
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const {
    currentUser: {
      featureToggles: { accumulatorAllowOverrideHedge: allowHedgeAmountOverride },
    },
  } = useUserContext();

  const tileId = useForwardAccumulatorTileId();
  const check = useCheckForwardAccumulatorByTradeCapture();

  const { hedgeAmount, hedgeCurrencyIndex } = useAppSelector((state) =>
    selectForwardAccumulatorHedgeData(state, tileId),
  );

  const rfsHedgeAmount = useForwardAccumulatorSelector((accumulator) =>
    selectForwardAccumulatorRfsHedgeAmount(accumulator),
  );

  const currencyPair = useAppSelector((state) => selectForwardAccumulatorProperty(state, tileId, 'currencyPair'));
  const [currency1, currency2] = currencyPair ? currencyPair.split('/') : ['', ''];

  const validation = useAppSelector((state) => selectForwardAccumulatorValidation(state, tileId));

  const isPinned = isDefined(hedgeAmount);

  const handleHedgeAmountChanged = (amount: number | null | undefined) => {
    sgwtWebAnalytics?.trackEvent('ForwardAccumulator', 'Hedge amount selected', amount?.toString() ?? '');

    check({
      hedgeAmount: amount,
      hedgeCurrencyIndex,
    });
  };

  const onCurrencyChange = () => {
    sgwtWebAnalytics?.trackEvent(
      'ForwardAccumulator',
      'Hedge currency changed',
      hedgeCurrencyIndex === 1 ? currency2 : currency1,
    );

    check({
      hedgeAmount,
      hedgeCurrencyIndex: hedgeCurrencyIndex === 1 ? 2 : 1,
    });
  };

  return (
    <FormField formattedMessageId="targetAccumulator.form.hedgeAmount">
      {allowHedgeAmountOverride ? (
        <div className="d-flex px-1">
          <TradeCaptureValidationWrapper validation={validation?.hedgeAmount}>
            <SgAmountInput
              size="sm"
              data-e2e="HedgeNotional"
              value={hedgeAmount ?? (isDefined(rfsHedgeAmount) ? Number(rfsHedgeAmount) : undefined)}
              className={isPinned ? 'text-info fw-bold ' : undefined}
              unit={hedgeCurrencyIndex === 1 ? currency1 : currency2}
              onChange={handleHedgeAmountChanged}
              onUnitClick={onCurrencyChange}
              allowNegative={false}
            />
          </TradeCaptureValidationWrapper>
          {isPinned && (
            <button
              onClick={() => handleHedgeAmountChanged(null)}
              type="button"
              className="btn btn-sm btn-icon btn-flat-primary"
              data-e2e="spotRef-pin"
            >
              <em className="icon icon-sm cursor-default line-height-1 text-secondary">push_pin</em>
            </button>
          )}
        </div>
      ) : (
        <SgAmountInput
          size="sm"
          data-e2e="HedgeNotional"
          value={isDefined(rfsHedgeAmount) ? Number(rfsHedgeAmount) : undefined}
          unit={hedgeCurrencyIndex === 1 ? currency1 : currency2}
          disabled
        />
      )}
    </FormField>
  );
}
